import { useCallback, useMemo, useState } from "react";
import { IDatabaseLineItem } from "../../shared/common/EstimateEngine/EstimateEngineTypes";
import { Table, TableRecord } from "./Table";
import { collection, CollectionReference } from "firebase/firestore";
import { useFirestore, useFirestoreCollectionData } from "reactfire";
import SharedUtils from "../../shared/common/SharedUtils";
import { ComplementsRuleChild, Rule, RuleRunCriteria, RuleRunCriteriaField, RuleTemplate } from "@rebuild/shared";
import { Organization, useOrganizations } from "../../hooks/rebuildBackendFetchHooks";
import { Tpa as TPA, Carrier } from "@rebuild/shared/models";
import { useFirestoreCollectionReadWrite } from "../../hooks/useFirestoreCollectionReadWrite";
import LoadingAnimation from "../../components/ui/utils/LoadingAnimation";

interface RulesTableProps {
	rules: Rule[];
	ruleTemplates: RuleTemplate[];
	onRuleSelect: (rule: Rule) => void;
}

const getOrgCellString = (orgRunCriteria: RuleRunCriteriaField | undefined, organizations: Organization[] | undefined): string | undefined => {
	if (orgRunCriteria === undefined || !organizations?.length) {
		return undefined;
	}

	const orgNames = orgRunCriteria.ids.map((id) => organizations.find((organization) => organization.organizationId === id)?.organizationName ?? "");

	return `${orgRunCriteria.includeOrExclude} ${orgNames.join(", ")}`;
};

const getTpaCellString = (tpaRunCriteria: RuleRunCriteriaField | undefined, tpas: TPA[] | undefined): string | undefined => {
	if (tpaRunCriteria === undefined || !tpas?.length) {
		return undefined;
	}

	const tpaNames = tpaRunCriteria.ids.map((id) => tpas.find((tpa) => tpa.id === id)?.name ?? "");

	return `${tpaRunCriteria.includeOrExclude} ${tpaNames.join(", ")}`;
};

const getCarrierCellString = (carrierRunCriteria: RuleRunCriteriaField | undefined, carriers: Carrier[] | undefined): string | undefined => {
	if (carrierRunCriteria === undefined || !carriers?.length) {
		return undefined;
	}

	const carrierNames = carrierRunCriteria.ids.map((id) => carriers.find((carrier) => carrier.id === id)?.name ?? "");

	return `${carrierRunCriteria.includeOrExclude} ${carrierNames.join(", ")}`;
};

/**
 * This component is used to display all the rules in our rule engine. There are two main parts of this component: the filters and the actual
 * table.
 *
 * The table itself shows all the inputs for each rule. The first set of columns contains the rule type, rule name, and run criteria for each
 * rule (org id, cat #, etc). The rest of the columns contain input values for these rules.  Some of the input values for different rule types
 * are similar enough that they can be put under the same column (e.g. "quantity conversion type" in line-item-transformation and "quantity
 * function" in complements-and-recommendations are both placed in the "Quantity Function" column.)
 *
 * This component also has a filter component that lets the user filter the rules based on the values contained in each column. The user can
 * add and remove filters. Each filter contains a drop-down of each header that the user can use to select which column they want to filter.
 */
export const RulesTable: React.FC<RulesTableProps> = ({ rules, ruleTemplates, onRuleSelect }) => {
	const firestoreToolsCollectionRef = collection(useFirestore(), SharedUtils.getDbPath("tools")) as CollectionReference<IDatabaseLineItem>;
	const dbLineItemsCol = useFirestoreCollectionData<IDatabaseLineItem>(firestoreToolsCollectionRef, { idField: "id" });

	const [selectedTemplateId, setSelectedTemplateId] = useState<string | undefined>(undefined);

	const [isDev, setIsDev] = useState<boolean>(false);

	const rulesFiltered = useMemo(() => {
		return (rules ?? []).filter((rule) => rule.isDev === isDev);
	}, [rules, isDev]);

	const { data: organizations, error } = useOrganizations();
	const { objects: tpas } = useFirestoreCollectionReadWrite<TPA>("tpa_objects");
	const { objects: carriers } = useFirestoreCollectionReadWrite<Carrier>("carrier_objects");

	const tableRecords = useMemo((): TableRecord[] => {
		if (dbLineItemsCol.status === "loading") {
			return [];
		}

		const dbLineItems = dbLineItemsCol.data;

		const _tableRecords: TableRecord[] = [];

		for (let i = 0; i < rulesFiltered.length; i++) {
			const rule = rulesFiltered[i];

			if (!!selectedTemplateId && rule.templateId !== selectedTemplateId) {
				continue;
			}

			const __backgroundColor = i % 2 === 0 ? "#F9F9F9" : "white";

			let tableRecord: TableRecord = {
				..._getBaseColumns(rule, onRuleSelect),
				"Organization Id": getOrgCellString(rule.runCriteria?.organizations, organizations),
				TPA: getTpaCellString(rule.runCriteria?.tpas, tpas),
				Carrier: getCarrierCellString(rule.runCriteria?.carriers, carriers),
				..._getRunCriteriaAsTableRecord(rule?.runCriteria),
				__backgroundColor,
			};

			let overrideTableRecords: TableRecord[] = [];

			if (rule.templateId === "complements_and_recommendations") {
				const baseParentLineItem = dbLineItems.find((dbLineItem) => dbLineItem.id === rule.ruleInputs.parent?.lineItemId);
				const baseChildLineItem = dbLineItems.find((dbLineItem) => dbLineItem.id === rule.ruleInputs.child?.lineItem?.lineItemId);

				tableRecord = {
					...tableRecord,
					"Room Type": rule.ruleInputs.roomTypes?.join(",") ?? "",
					"Parent Line Item": baseParentLineItem?.originalLineItem ?? "",
					"Selected Values": JSON.stringify(rule.ruleInputs.parent?.selectedValues),
					...(baseChildLineItem ? _getComplimentChildFieldsAsTableRecord(baseChildLineItem, rule.ruleInputs.child) : {}),
				};

				for (const override of rule.ruleInputs.overrides ?? []) {
					const overrideChildLineItem = dbLineItems.find((dbLineItem) => dbLineItem.id === rule.ruleInputs.child?.lineItem?.lineItemId);
					const selectedValues = {
						...(rule.ruleInputs.parent?.selectedValues ?? {}),
						...(override.permutation?.selectedValues ?? {}),
					};

					overrideTableRecords.push({
						..._getBaseColumns(rule, onRuleSelect),
						"Organization Id": getOrgCellString(override?.runCriteria?.organizations, organizations),
						TPA: getTpaCellString(override?.runCriteria?.tpas, tpas),
						Carrier: getCarrierCellString(override?.runCriteria?.carriers, carriers),
						..._getRunCriteriaAsTableRecord(override?.runCriteria),
						"Room Type": override.roomTypes?.join(",") ?? "",
						"Parent Line Item": baseParentLineItem?.originalLineItem ?? "",
						"Selected Values": JSON.stringify(selectedValues),
						...(override.child && overrideChildLineItem ? _getComplimentChildFieldsAsTableRecord(overrideChildLineItem, override.child) : {}),
					});
				}
			} else if (rule.templateId === "line_item_transformation") {
				const baseInitialLineItem = dbLineItems.find((dbLineItem) => dbLineItem.id === rule.ruleInputs.initialLineItem?.lineItemId);
				const baseFinalLineItem = dbLineItems.find((dbLineItem) => dbLineItem.id === rule.ruleInputs.finalLineItem?.lineItemId);

				tableRecord = {
					...tableRecord,
					"Parent Line Item": baseInitialLineItem?.originalLineItem ?? "",
					"Selected Values": JSON.stringify(rule.ruleInputs.initialLineItem?.selectedValues),
					"Quantity Function": rule.ruleInputs.quantityConversionType,
					"Quantity Value": rule.ruleInputs.quantityConversionValue,
					"Final Line Item": baseFinalLineItem?.originalLineItem ?? "",
					"Final Permutation": JSON.stringify(rule.ruleInputs.finalLineItem?.selectedPermutation),
				};

				for (const override of rule.ruleInputs.overrides ?? []) {
					const baseOverrideLineItem = dbLineItems.find((dbLineItem) => dbLineItem.id === override.permutation?.lineItemId);
					const baseFinalOverrideLineItem = dbLineItems.find((dbLineItem) => dbLineItem.id === override.permutation?.lineItemId);

					overrideTableRecords.push({
						..._getBaseColumns(rule, onRuleSelect),
						"Organization Id": getOrgCellString(override?.runCriteria?.organizations, organizations),
						TPA: getTpaCellString(override?.runCriteria?.tpas, tpas),
						Carrier: getCarrierCellString(override?.runCriteria?.carriers, carriers),
						..._getRunCriteriaAsTableRecord(override?.runCriteria),
						"Parent Line Item": baseOverrideLineItem?.originalLineItem ?? "",
						"Selected Values": JSON.stringify(override.permutation?.selectedValues),
						"Quantity Function": override.quantityConversionType,
						"Quantity Value": override.quantityConversionValue,
						"Final Line Item": baseFinalOverrideLineItem?.originalLineItem ?? "",
						"Final Permutation": JSON.stringify(override.finalLineItem?.selectedPermutation),
					});
				}
			} else if (rule.templateId === "mutually_exclusive") {
				const baseExcludeeLineItem = dbLineItems.find((dbLineItem) => dbLineItem.id === rule.ruleInputs?.excludee?.lineItemId);

				tableRecord = {
					...tableRecord,
					"Excluded Line Item": baseExcludeeLineItem?.originalLineItem,
					"Excluded Line Item Selected Values": JSON.stringify(rule.ruleInputs.excludee.selectedValues),
				};

				for (const excluder of rule.ruleInputs.excluders) {
					const baseExcludingLineItem = dbLineItems.find((dbLineItem) => dbLineItem.id === excluder?.excluder?.lineItemId);

					overrideTableRecords.push({
						..._getBaseColumns(rule, onRuleSelect),
						"Excluding Line Item": baseExcludingLineItem?.originalLineItem,
						"Excluding Line Item Selected Values": JSON.stringify(excluder.excluder.selectedValues),
					});
				}
			}

			_tableRecords.push(tableRecord);
			if (overrideTableRecords.length > 0) {
				_tableRecords.push(...overrideTableRecords.map((tableRecord) => ({ ...tableRecord, __backgroundColor })));
			}
		}

		return _tableRecords;
	}, [rulesFiltered, dbLineItemsCol, selectedTemplateId, organizations, tpas, carriers]);

	const uniqueTableKeys = useMemo(
		() =>
			Array.from(new Set(tableRecords.flatMap((tableRecord) => Object.keys(tableRecord)))).filter(
				(key) => !["__backgroundColor", "Action"].includes(key),
			),
		[tableRecords],
	);

	const [filters, setFilters] = useState<{ column: string; value: string }[]>([]);

	const updateFilterColumn = (index: number, column: string) => {
		const updatedFilters = [...filters];
		updatedFilters[index].column = column;
		setFilters(updatedFilters);
	};

	const updateFilterValue = (index: number, value: string) => {
		const updatedFilters = [...filters];
		updatedFilters[index].value = value;
		setFilters(updatedFilters);
	};

	const addFilter = () => {
		setFilters([...filters, { column: "", value: "" }]);
	};

	const removeFilter = (index: number) => {
		setFilters(filters.filter((_, i) => i !== index));
	};

	const tableRecordsFiltered = useMemo(() => {
		const _normalizeStr = (str: string) => str.toLowerCase().replaceAll(/\s/gi, "");

		const filtersToApply = filters.filter(({ value }) => value !== "");

		return tableRecords.filter((tableRecord) =>
			filtersToApply.every(({ column, value }) => !!tableRecord[column] && _normalizeStr(tableRecord[column] as string)?.includes(_normalizeStr(value))),
		);
	}, [tableRecords, filters]);

	return (
		<div className="space-y-4">
			<div className="flex items-center gap-8">
				<div className="flex gap-2 items-center">
					<label htmlFor="template-filter" className="text-sm font-medium text-gray-700">
						Filter by Template:
					</label>
					<select
						id="template-filter"
						value={selectedTemplateId || ""}
						onChange={(e) => setSelectedTemplateId(e.target.value || undefined)}
						className="px-3 py-2 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
					>
						<option value="">All Templates</option>
						{ruleTemplates.map((template) => (
							<option key={template.id} value={template.id}>
								{template.name}
							</option>
						))}
					</select>
				</div>
				<div className="flex gap-2 items-center">
					<label htmlFor="template-filter" className="text-sm font-medium text-gray-700">
						Filter by Environment:
					</label>
					<div className="flex">
						<button
							onClick={() => setIsDev(false)}
							className={`px-4 py-2 text-sm font-medium rounded-l-lg border transition-colors ${
								!isDev ? "bg-blue-600 text-white border-blue-600" : "bg-white text-gray-700 border-gray-300 hover:bg-gray-50"
							}`}
						>
							Prod
						</button>
						<button
							onClick={() => setIsDev(true)}
							className={`px-4 py-2 text-sm font-medium rounded-r-lg border-l-0 border transition-colors ${
								isDev ? "bg-blue-600 text-white border-blue-600" : "bg-white text-gray-700 border-gray-300 hover:bg-gray-50"
							}`}
						>
							Dev
						</button>
					</div>
				</div>
			</div>
			<div className="mb-4 p-4 bg-gray-50 border border-gray-200 rounded">
				<div className="flex items-center justify-between mb-3">
					<h3 className="font-semibold text-gray-700">Filters</h3>
					<button onClick={addFilter} className="px-3 py-1 text-sm bg-blue-500 text-white rounded hover:bg-blue-600">
						Add Filter
					</button>
				</div>
				<div className="space-y-2">
					{filters.map((filter, index) => (
						<div key={index} className="flex gap-2 items-center">
							<select
								value={filter.column}
								onChange={(e) => updateFilterColumn(index, e.target.value)}
								className="px-3 py-1 border border-gray-300 rounded text-sm"
							>
								<option value="">Select column...</option>
								{uniqueTableKeys.map((col) => (
									<option key={col} value={col}>
										{col}
									</option>
								))}
							</select>

							<input
								type="text"
								placeholder="Filter value..."
								value={filter.value}
								onChange={(e) => updateFilterValue(index, e.target.value)}
								className="px-3 py-1 border border-gray-300 rounded text-sm flex-1"
							/>

							<button onClick={() => removeFilter(index)} className="px-2 py-1 text-sm bg-red-500 text-white rounded hover:bg-red-600">
								Remove
							</button>
						</div>
					))}
				</div>
			</div>
			<div className="h-[80vh] overflow-y-auto">
				{dbLineItemsCol.status === "loading" ? <LoadingAnimation fullScreen={false} /> : <Table records={tableRecordsFiltered} />}
			</div>
		</div>
	);
};

const _getBaseColumns = (rule: Rule, onRuleSelect: (rule: Rule) => void) => ({
	Action: (
		<button onClick={() => onRuleSelect(rule)} className="bg-blue-500 hover:bg-blue-600 text-white font-medium py-1 px-3 rounded text-xs transition-colors">
			Update
		</button>
	),
	"Rule Type": rule.templateId,
	"Rule Name": rule.name,
});

const _getRunCriteriaAsTableRecord = (runCriteria?: RuleRunCriteria) => ({
	"Cat #": runCriteria?.catNumbers?.join(", ") ?? "",
	"Estimate Type": runCriteria?.estimateType ?? "",
});

const _getComplimentChildFieldsAsTableRecord = (dbLineItem: IDatabaseLineItem, child: ComplementsRuleChild) =>
	child
		? {
				"Child Line Item Original Text": dbLineItem.originalLineItem,
				"Child Line Item Permutations": child.lineItem?.selectedPermutation,
				"Quantity Function": child.quantityFunction,
				"Quantity Value": child.quantityValue,
				"Is Likely": child.isLikely ? "yes" : "",
			}
		: {};
